import { useState, useEffect, Fragment } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Transition, Dialog } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/20/solid';
import MaterialDrawer from '../components/MaterialDrawer';

function StandardPOs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreatePOModalOpen, setIsCreatePOModalOpen] = useState(false);
  const [expandedPOs, setExpandedPOs] = useState(new Set());
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [newLineItem, setNewLineItem] = useState({
    po_line_number: '',
    part_number: '',
    description: '',
    quantity_ordered: '',
    unit_price: '',
  });
  const [newLineItemSearch, setNewLineItemSearch] = useState('');
  const [showPartDropdown, setShowPartDropdown] = useState(false);
  const [parts, setParts] = useState([]);
  const [shops, setShops] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [filterShop, setFilterShop] = useState('');
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [sortField, setSortField] = useState('po_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [excludeClosed, setExcludeClosed] = useState(true);
  const [materialDrawerOpen, setMaterialDrawerOpen] = useState(false);
  const [selectedJobForMaterial, setSelectedJobForMaterial] = useState(null);

  useEffect(() => {
    document.title = 'Standard POs';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch standard jobs (where kanban_job_id is null)
        const { data: jobsData, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, description, status,
            quantity_on_order, produced_quantity, shipped_quantity,
            expected_delivery_date, po_start_date, material_status, material_notes,
            shops(id, name), customers(id, name, nickname),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .is('kanban_job_id', null)
          .order('po_number, po_line_number');
        if (jobsError) throw jobsError;

        const formattedJobs = jobsData.map(job => ({
          id: job.id,
          po_number: job.po_number,
          po_line_number: job.po_line_number,
          part_number: job.part_number,
          description: job.description,
          status: job.status,
          quantity_on_order: job.quantity_on_order,
          produced_quantity: job.produced_quantity,
          shipped_quantity: job.shipped_quantity,
          remaining_quantity: job.quantity_on_order - job.shipped_quantity,
          expected_delivery_date: job.expected_delivery_date,
          po_start_date: job.po_start_date,
          shop_name: job.shops?.name || '-',
          customer_name: job.customers?.nickname || job.customers?.name || '-',
          inventory_quantity: job.parts?.inventory_quantity || 0,
          material_status: job.material_status || null,
          material_notes: job.material_notes || '',
        }));

        setJobs(formattedJobs);
        setFilteredJobs(formattedJobs);

        const { data: partsData, error: partsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description');
        if (partsError) throw partsError;
        setParts(partsData || []);

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, name, nickname')
          .order('name');
        if (customersError) throw customersError;
        setCustomers(customersData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Group jobs by PO number
  const groupedPOs = filteredJobs.reduce((acc, job) => {
    if (!acc[job.po_number]) {
      acc[job.po_number] = {
        po_number: job.po_number,
        po_start_date: job.po_start_date,
        next_due_date: job.expected_delivery_date,
        customer_name: job.customer_name,
        shop_name: job.shop_name,
        lineItems: [],
        totalOrdered: 0,
        totalShipped: 0,
        totalProduced: 0,
      };
    }
    // Track the earliest due date across all line items
    if (job.expected_delivery_date) {
      if (!acc[job.po_number].next_due_date ||
          new Date(job.expected_delivery_date) < new Date(acc[job.po_number].next_due_date)) {
        acc[job.po_number].next_due_date = job.expected_delivery_date;
      }
    }
    acc[job.po_number].lineItems.push(job);
    acc[job.po_number].totalOrdered += job.quantity_on_order;
    acc[job.po_number].totalShipped += job.shipped_quantity;
    acc[job.po_number].totalProduced += job.produced_quantity;
    return acc;
  }, {});

  const poList = Object.values(groupedPOs).sort((a, b) => {
    if (sortField === 'po_number') {
      return sortDirection === 'asc'
        ? a.po_number.localeCompare(b.po_number)
        : b.po_number.localeCompare(a.po_number);
    }
    if (sortField === 'next_due_date') {
      return sortDirection === 'asc'
        ? new Date(a.next_due_date || '9999-12-31') - new Date(b.next_due_date || '9999-12-31')
        : new Date(b.next_due_date || '9999-12-31') - new Date(a.next_due_date || '9999-12-31');
    }
    if (sortField === 'line_count') {
      return sortDirection === 'asc'
        ? a.lineItems.length - b.lineItems.length
        : b.lineItems.length - a.lineItems.length;
    }
    return 0;
  });

  const togglePOExpanded = (poNumber) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(poNumber)) {
        next.delete(poNumber);
      } else {
        next.add(poNumber);
      }
      return next;
    });
  };

  const openMaterialDrawer = (job) => {
    setSelectedJobForMaterial(job);
    setMaterialDrawerOpen(true);
  };

  const handleMaterialUpdate = (updatedJob) => {
    const updateJob = (job) =>
      job.id === updatedJob.id
        ? { ...job, material_status: updatedJob.material_status, material_notes: updatedJob.material_notes }
        : job;

    setJobs(prev => prev.map(updateJob));
    setFilteredJobs(prev => prev.map(updateJob));
  };

  const addLineItemToList = () => {
    if (!newLineItem.part_number || !newLineItem.quantity_ordered) {
      toast.error('Please select a part and enter a quantity');
      return;
    }
    // Auto-assign next line number if not specified
    const lineNumber = newLineItem.po_line_number || (Math.max(0, ...lineItems.map(i => i.po_line_number || 0)) + 1);
    setLineItems([...lineItems, { ...newLineItem, po_line_number: lineNumber }]);
    setNewLineItem({
      po_line_number: '',
      part_number: '',
      description: '',
      quantity_ordered: '',
      unit_price: '',
    });
    setNewLineItemSearch('');
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handlePartSelect = (part) => {
    setNewLineItem({
      ...newLineItem,
      part_number: part.part_number,
      description: part.description,
      unit_price: part.unit_price || '',
    });
    setNewLineItemSearch(part.part_number);
    setShowPartDropdown(false);
  };

  const handleCreateNewPart = async () => {
    if (!newLineItemSearch) return;
    const newPart = {
      part_number_master: newLineItemSearch,
      revision: '0',
      description: `New Part: ${newLineItemSearch}`,
      inventory_quantity: 0,
    };
    try {
      const { data, error } = await supabase
        .from('parts')
        .insert(newPart)
        .select('part_number, description')
        .single();
      if (error) throw error;
      setParts([...parts, data]);
      handlePartSelect(data);
      setShowPartDropdown(false);
    } catch (err) {
      console.error('Error creating part:', err);
      toast.error('Failed to create new part: ' + err.message);
    }
  };

  const handleSavePO = async () => {
    if (
      !poNumber ||
      !dueDate ||
      !selectedCustomer ||
      lineItems.length === 0 ||
      lineItems.some(
        item =>
          !item.part_number ||
          !item.quantity_ordered
      )
    ) {
      setError('Please fill in all required fields and add at least one line item.');
      return;
    }

    const newJobs = lineItems.map((item) => ({
      po_number: poNumber,
      po_line_number: item.po_line_number,
      part_number: item.part_number,
      description: item.description,
      quantity_on_order: parseFloat(item.quantity_ordered),
      unit_price: parseFloat(item.unit_price),
      po_start_date: poDate || null,
      expected_delivery_date: dueDate,
      status: 'not_started',
      shop_id: 1,
      customer_id: parseInt(selectedCustomer),
      produced_quantity: 0,
      shipped_quantity: 0,
    }));

    try {
      const { error } = await supabase.from('jobs').insert(newJobs);
      if (error) throw error;

      // Refetch jobs data
      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status,
          quantity_on_order, produced_quantity, shipped_quantity,
          expected_delivery_date, po_start_date, shops(id, name), customers(id, name, nickname),
          parts!inner(part_number, inventory_quantity)
        `)
        .is('kanban_job_id', null)
        .order('po_number, po_line_number');
      if (fetchError) throw fetchError;

      const formattedJobs = data.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        remaining_quantity: job.quantity_on_order - job.shipped_quantity,
        expected_delivery_date: job.expected_delivery_date,
        po_start_date: job.po_start_date,
        shop_name: job.shops?.name || '-',
        customer_name: job.customers?.nickname || job.customers?.name || '-',
        inventory_quantity: job.parts?.inventory_quantity || 0,
      }));

      setJobs(formattedJobs);
      setFilteredJobs(formattedJobs);

      setIsCreatePOModalOpen(false);
      setPoNumber('');
      setPoDate('');
      setDueDate('');
      setSelectedCustomer('');
      setLineItems([]);
      setNewLineItem({ part_number: '', description: '', quantity_ordered: '', unit_price: '' });
      setNewLineItemSearch('');
      setError(null);
      toast.success('Purchase Order created successfully!');
    } catch (err) {
      console.error('Error saving PO:', err);
      setError('Failed to save PO: ' + err.message);
      toast.error(`Failed to save PO: ${err.message}`);
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const handleFilter = () => {
    let filtered = [...jobs];
    if (filterShop) {
      filtered = filtered.filter(job => job.shop_name === filterShop);
    }
    if (filterCustomer) {
      filtered = filtered.filter(job => job.customer_name === filterCustomer);
    }
    if (searchQuery) {
      filtered = filtered.filter(job =>
        job.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.part_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (excludeClosed) {
      filtered = filtered.filter(job => job.status !== 'closed');
    }
    setFilteredJobs(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [jobs, filterShop, filterCustomer, searchQuery, excludeClosed]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const getOverallStatus = (lineItems) => {
    const allClosed = lineItems.every(item => item.status === 'closed');
    if (allClosed) return { label: 'Closed', class: 'bg-slate-100 text-slate-600' };

    const anyInProduction = lineItems.some(item => item.status === 'in_production');
    if (anyInProduction) return { label: 'In Production', class: 'bg-amber-100 text-amber-700' };

    const anyShipped = lineItems.some(item => item.shipped_quantity > 0);
    if (anyShipped) return { label: 'Partially Shipped', class: 'bg-blue-100 text-blue-700' };

    return { label: 'Not Started', class: 'bg-slate-100 text-slate-500' };
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="w-4 h-4 inline ml-1" />
      : <ChevronDownIcon className="w-4 h-4 inline ml-1" />;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <p className="text-slate-500">Loading...</p>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header Card */}
      <div className="bg-white border-b border-slate-200 h-12 px-4 flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-800">Standard POs</h1>
            <span className="text-xs text-slate-500">{poList.length} purchase order{poList.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search PO or part..."
              className="w-56 px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-slate-50"
            />
            <div className="relative">
              <button
                onClick={() => setShowShopDropdown(!showShopDropdown)}
                onBlur={() => setTimeout(() => setShowShopDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className={filterShop ? 'text-slate-800' : 'text-slate-500'}>
                  {filterShop || 'All Shops'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showShopDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[150px]">
                  <div
                    onClick={() => { setFilterShop(''); setShowShopDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterShop ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Shops
                  </div>
                  {shops.map(shop => (
                    <div
                      key={shop.id}
                      onClick={() => { setFilterShop(shop.name); setShowShopDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterShop === shop.name ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {shop.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[140px] justify-between"
              >
                <span className={filterCustomer ? 'text-slate-800' : 'text-slate-500'}>
                  {filterCustomer || 'All Customers'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showCustomerDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[180px] max-h-64 overflow-y-auto">
                  <div
                    onClick={() => { setFilterCustomer(''); setShowCustomerDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterCustomer ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Customers
                  </div>
                  {customers.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => { setFilterCustomer(customer.nickname || customer.name); setShowCustomerDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterCustomer === (customer.nickname || customer.name) ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {customer.nickname || customer.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setExcludeClosed(!excludeClosed)}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                excludeClosed
                  ? 'bg-slate-700 text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              Hide Closed
            </button>
            <button
              onClick={() => setIsCreatePOModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create PO
            </button>
          </div>
        </div>
      </div>

      {/* PO Table */}
      <div className="bg-white border-b border-slate-200">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="w-8 px-3 py-2"></th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                onClick={() => handleSort('po_number')}
              >
                PO Number <SortIcon field="po_number" />
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Customer
              </th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                onClick={() => handleSort('line_count')}
              >
                Lines <SortIcon field="line_count" />
              </th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                onClick={() => handleSort('next_due_date')}
              >
                Next Due <SortIcon field="next_due_date" />
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Progress
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {poList.map((po) => {
              const isExpanded = expandedPOs.has(po.po_number);
              const progress = po.totalOrdered > 0 ? Math.round((po.totalShipped / po.totalOrdered) * 100) : 0;
              const status = getOverallStatus(po.lineItems);

              return (
                <Fragment key={po.po_number}>
                  {/* PO Row */}
                  <tr
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => togglePOExpanded(po.po_number)}
                  >
                    <td className="px-3 py-2.5">
                      <ChevronRightIcon
                        className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/jobs/${po.po_number}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-slate-800 hover:text-blue-600 transition-colors"
                      >
                        {po.po_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-slate-600">
                      {po.customer_name}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-slate-600 tabular-nums">
                      {po.lineItems.length}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-slate-600 tabular-nums">
                      {formatDate(po.next_due_date)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-slate-600 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 tabular-nums w-8">{progress}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm ${status.class}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded Line Items */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-slate-50 p-0">
                        <div className="border-t border-slate-200">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Line</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Description</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Ordered</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Produced</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shipped</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Inventory</th>
                                <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Material</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                              {po.lineItems.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50">
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{item.po_line_number}</td>
                                  <td className="px-3 py-2 text-sm">
                                    <Link
                                      to={`/jobs/${item.po_number}`}
                                      className="font-medium text-slate-700 hover:text-blue-600 transition-colors"
                                    >
                                      {item.part_number}
                                    </Link>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-600 max-w-xs truncate" title={item.description}>
                                    {item.description || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{item.quantity_on_order}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{item.produced_quantity}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{item.shipped_quantity}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{item.inventory_quantity}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openMaterialDrawer(item); }}
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-medium cursor-pointer transition-colors ${
                                        item.material_status === 'no_stock'
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : item.material_status === 'ordered'
                                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                          : item.material_status === 'ready'
                                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                      }`}
                                      title={item.material_notes || 'Click to edit material info'}
                                    >
                                      {item.material_status === 'no_stock' ? (
                                        <>
                                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                          No Stock
                                        </>
                                      ) : item.material_status === 'ordered' ? (
                                        <>
                                          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                                          Ordered
                                        </>
                                      ) : item.material_status === 'ready' ? (
                                        <>
                                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                          Ready
                                        </>
                                      ) : (
                                        '—'
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {poList.length === 0 && (
          <div className="px-6 py-12 text-center">
            <p className="text-slate-500">No Standard POs found.</p>
          </div>
        )}
      </div>

      {/* Create PO Modal */}
      <Transition appear show={isCreatePOModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsCreatePOModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <Dialog.Title className="text-sm font-semibold text-slate-800">
                      Create Standard PO
                    </Dialog.Title>
                    <button
                      onClick={() => setIsCreatePOModalOpen(false)}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <span className="sr-only">Close</span>
                      ×
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                      <div className="mb-4 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm">
                        {error}
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                          PO Number
                        </label>
                        <input
                          type="text"
                          value={poNumber}
                          onChange={(e) => setPoNumber(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                          Customer
                        </label>
                        <select
                          value={selectedCustomer}
                          onChange={(e) => setSelectedCustomer(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                          required
                        >
                          <option value="">Select...</option>
                          {customers.map(customer => (
                            <option key={customer.id} value={customer.id}>{customer.nickname || customer.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                          PO Date
                        </label>
                        <input
                          type="date"
                          value={poDate}
                          onChange={(e) => setPoDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          required
                        />
                      </div>
                    </div>

                    {/* Add Line Item Section */}
                    <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Add Line Item</h3>
                      <div className="grid grid-cols-6 gap-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Line #</label>
                          <input
                            type="number"
                            value={newLineItem.po_line_number}
                            onChange={(e) => setNewLineItem({ ...newLineItem, po_line_number: parseInt(e.target.value) || '' })}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                            placeholder="Auto"
                            min="1"
                          />
                        </div>
                        <div className="col-span-2 relative">
                          <label className="block text-xs text-slate-500 mb-1">Part Number</label>
                          <input
                            type="text"
                            value={newLineItemSearch}
                            onChange={(e) => {
                              setNewLineItemSearch(e.target.value);
                              setShowPartDropdown(true);
                            }}
                            onFocus={() => setShowPartDropdown(true)}
                            onBlur={() => setTimeout(() => setShowPartDropdown(false), 150)}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            placeholder="Search parts..."
                          />
                          {showPartDropdown && newLineItemSearch && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                              {parts.filter(part =>
                                part.part_number.toLowerCase().includes(newLineItemSearch.toLowerCase()) ||
                                (part.description && part.description.toLowerCase().includes(newLineItemSearch.toLowerCase()))
                              ).slice(0, 10).map((part) => (
                                <div
                                  key={part.part_number}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handlePartSelect(part)}
                                  className="px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                                >
                                  <span className="font-medium text-slate-800">{part.part_number}</span>
                                  <span className="text-slate-500 ml-2">{part.description}</span>
                                </div>
                              ))}
                              {parts.filter(part =>
                                part.part_number.toLowerCase().includes(newLineItemSearch.toLowerCase())
                              ).length === 0 && (
                                <div
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleCreateNewPart()}
                                  className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer"
                                >
                                  Create new part: "{newLineItemSearch}"
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={newLineItem.quantity_ordered}
                            onChange={(e) => setNewLineItem({ ...newLineItem, quantity_ordered: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Unit Price</label>
                          <input
                            type="number"
                            value={newLineItem.unit_price}
                            onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={addLineItemToList}
                            className="w-full px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                      {newLineItem.description && (
                        <p className="mt-2 text-xs text-slate-500">Description: {newLineItem.description}</p>
                      )}
                    </div>

                    {/* Line Items Table */}
                    <div className="mb-4">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        Line Items ({lineItems.length})
                      </h3>
                      <div className="border border-slate-200 rounded-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-16">Line</th>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Description</th>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-24">Qty</th>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-24">Price</th>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-16"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {lineItems.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="px-3 py-4 text-sm text-slate-500 text-center">
                                  No line items added yet. Use the form above to add items.
                                </td>
                              </tr>
                            ) : (
                              lineItems.map((item, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={item.po_line_number || ''}
                                      onChange={(e) => updateLineItem(index, 'po_line_number', parseInt(e.target.value) || '')}
                                      className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                      min="1"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-sm font-medium text-slate-800">{item.part_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{item.description}</td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={item.quantity_ordered}
                                      onChange={(e) => updateLineItem(index, 'quantity_ordered', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                      min="0"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      onClick={() => removeLineItem(index)}
                                      className="text-xs text-slate-400 hover:text-red-600 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
                    <button
                      onClick={() => setIsCreatePOModalOpen(false)}
                      className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePO}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                    >
                      Create PO
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Material Drawer */}
      <MaterialDrawer
        isOpen={materialDrawerOpen}
        onClose={() => setMaterialDrawerOpen(false)}
        job={selectedJobForMaterial}
        onUpdate={handleMaterialUpdate}
      />
    </div>
  );
}

export default StandardPOs;
